import { get } from 'lodash';
import { config, msg } from 'api';
import { setSuccessMessage, setErrorMessage } from 'apps/notifications/actions/notifications';
import errorHandler from 'lib/utils/errorHandler';
import { pageEvents, pageStatistics } from 'apps/settings/analytics';
import { wa } from 'apps/analytics';

export const SET_DELIVERY_ADDRESSES = 'SET_DELIVERY_ADDRESSES';
export const ADDRESS_BOOK_SET_ADDRESSES_FOR_POSTCODE = 'ADDRESS_BOOK_SET_ADDRESSES_FOR_POSTCODE';
export const ADDRESS_BOOK_SET_POSTCODE_ERROR_CODE = 'ADDRESS_BOOK_SET_POSTCODE_ERROR_CODE';
export const ADDRESS_BOOK_SET_MANUAL_ADDRESS_ERROR = 'ADDRESS_BOOK_SET_MANUAL_ADDRESS_ERROR';
export const ADDRESS_BOOK_SET_CITY_ERROR = 'ADDRESS_BOOK_SET_CITY_ERROR';
export const ADDRESS_BOOK_TOGGLE_MANUAL_ADDRESS_ENTERING = 'ADDRESS_BOOK_TOGGLE_MANUAL_ADDRESS_ENTERING';
export const ADDRESS_BOOK_SET_DELIVERY_ADDRESS_TO_EDIT = 'ADDRESS_BOOK_SET_DELIVERY_ADDRESS_TO_EDIT';
export const ADDRESS_BOOK_RESET_ADD_ADDRESS_FORM = 'ADDRESS_BOOK_RESET_ADD_ADDRESS_FORM';
export const ADDRESS_BOOK_UPDATE_FIELD = 'ADDRESS_BOOK_UPDATE_FIELD';
export const SET_DELETE_DELIVERY_ADDRESS_ERROR = 'SET_DELETE_DELIVERY_ADDRESS_ERROR';
export const SET_OVERRIDE_DELIVERY_ADDRESS_ERROR = 'SET_OVERRIDE_DELIVERY_ADDRESS_ERROR';

export function setDeliveryAddresses(deliveryInfo) {
  return {
    type: SET_DELIVERY_ADDRESSES,
    deliveryAddresses: deliveryInfo.addresses,
    registrationPostcode: deliveryInfo.registrationPostcode,
  };
}

export function setDeleteDeliveryAddressError(errorCode) {
  return {
    type: SET_DELETE_DELIVERY_ADDRESS_ERROR,
    errorCode,
  };
}

export function setOverrideDeliveryAddressError(errorCode) {
  return {
    type: SET_OVERRIDE_DELIVERY_ADDRESS_ERROR,
    errorCode,
  };
}

export function getDeliveryAddresses() {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/settings/addresses`)
      .then(response => dispatch(setDeliveryAddresses(response)))
      .catch(errorHandler);
}

export function deleteDeliveryAddress(addressId) {
  return (dispatch, getState, { network }) =>
    network.del(`${config.apiBasePath}/settings/addresses/${addressId}`)
      .then(() => {
        dispatch(setSuccessMessage('Address deleted.'));
        wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_DELETE_ADDRESS_POPUP_CONFIRM, {
          [pageStatistics.NUMBER_OF_ADDRESSES]: getState().settings.deliveryAddresses.length - 1,
        });
        return dispatch(getDeliveryAddresses());
      })
      .catch((errorResponse) => {
        if (errorResponse.data && errorResponse.data.code) {
          return dispatch(setDeleteDeliveryAddressError(errorResponse.data.code));
        }
        return dispatch(setErrorMessage(msg.settings.flashMessage.failure));
      });
}

export function setDeliveryAddressesForPostcode(response) {
  return {
    type: ADDRESS_BOOK_SET_ADDRESSES_FOR_POSTCODE,
    ...response,
  };
}

export function resetAddAddressForm() {
  return {
    type: ADDRESS_BOOK_RESET_ADD_ADDRESS_FORM,
  };
}

export function setPostcodeError(postcode, postcodeErrorCode) {
  return {
    type: ADDRESS_BOOK_SET_POSTCODE_ERROR_CODE,
    postcode,
    postcodeErrorCode,
  };
}

export function setManualAddressError(manualAddressError) {
  return {
    type: ADDRESS_BOOK_SET_MANUAL_ADDRESS_ERROR,
    manualAddressError,
  };
}

export function setCityError(cityError) {
  return {
    type: ADDRESS_BOOK_SET_CITY_ERROR,
    cityError,
  };
}

export function updateField(field, value) {
  return {
    type: ADDRESS_BOOK_UPDATE_FIELD,
    field,
    value,
  };
}

function normalizePostcode(postcode) {
  return postcode ? postcode.replace(/\s/g, '').toUpperCase() : '';
}

export function updatePostcode(postcode) {
  return (dispatch, getState) => {
    const normalizedPrevPostcode = normalizePostcode(getState().settings.addAddress.postcode);
    const normalizedNextPostcode = normalizePostcode(postcode);
    if (normalizedPrevPostcode !== normalizedNextPostcode) {
      dispatch(resetAddAddressForm());
    }
    return dispatch(updateField('postcode', postcode));
  };
}

export function toggleManualAddressEntering() {
  return {
    type: ADDRESS_BOOK_TOGGLE_MANUAL_ADDRESS_ENTERING,
  };
}

export function setDeliveryAddress(address) {
  return {
    type: ADDRESS_BOOK_SET_DELIVERY_ADDRESS_TO_EDIT,
    address,
  };
}

export function getAddressesForPostcode(postcode) {
  const postcodeUppercaseWithoutSpaces = normalizePostcode(postcode);
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/addresses?postcode=${postcodeUppercaseWithoutSpaces}`)
      .then(response => {
        wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_ADD_ADDRESS_POSTCODE_VALID, {
          [pageStatistics.ADDRESS_OUT_OF_ZONE]: !response.canDeliver,
        });

        const isOspZone = response.deliveryAreaType === 'STORE_PICK';
        const displayOSPZoneOptions = isOspZone &&
          getState().user.betaFeatures.includes('OSPZONE_OPTIONS_IN_ADD_NEW_ADDRESS');

        if (!response.canDeliver && !displayOSPZoneOptions) {
          return dispatch(setPostcodeError(postcode, isOspZone ? 'OSPZone' : 'outOfZone'));
        }
        dispatch(resetAddAddressForm());
        return dispatch(setDeliveryAddressesForPostcode(response));
      })
      .catch(response => {
        if (get(response, 'status') === 412) {
          return dispatch(setPostcodeError(postcode, 'invalidPostcode'));
        }
        return dispatch(setPostcodeError(postcode, 'unknownError'));
      });
}

export function getAddressById(id) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/settings/addresses/${id}`)
      .then(response => {
        dispatch(setDeliveryAddress(response));
      })
      .catch(errorHandler);
}

export function updateAddress(id, address) {
  return (dispatch, getState, { network }) => {
    wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_EDIT_ADDRESS_SAVE_BUTTON_CLICKED, {
      [pageStatistics.ADDRESS_NICKNAME_FILLED]: !!address.addressNickname,
      [pageStatistics.DELIVERY_INSTRUCTIONS_FILLED]: !!address.deliveryInstructions,
    });

    return network.patch(`${config.apiBasePath}/settings/addresses/${id}`, {
      body: JSON.stringify({
        nickname: address.addressNickname,
        deliveryNotes: address.deliveryInstructions,
      }),
    })
      .then(() => {
        wa.triggerPageEventWithDescription(
          pageEvents.DELIVERY_ADDRESS_BOOK_EDIT_ADDRESS_SUCCESS_NOTIFICATION_DISPLAYED
        );
        network.redirectTo({ to: '/settings/address-book' });
        dispatch(setSuccessMessage(msg.settings.flashMessage.success));
        return true;
      })
      .catch(() => {
        wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_EDIT_ADDRESS_ERROR_NOTIFICATION_DISPLAYED);
        dispatch(setErrorMessage(msg.settings.flashMessage.failure));
        return false;
      });
  };
}

export function overrideDeliveryAddress(id, address) {
  return (dispatch, getState, { network }) => {
    wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_OVERRIDE_ADDRESS_SAVE_BUTTON_CLICKED, {
      [pageStatistics.ADDRESS_POSTCODE_FILLED]: !!address.postcode,
      [pageStatistics.ADDRESS_COMPANY_NAME_FILLED]: !!address.companyName,
      [pageStatistics.ADDRESS_PREFECTURE_FILLED]: !!address.prefecture,
      [pageStatistics.ADDRESS_STREET_TRANSCRIPTION_FILLED]: !!address.streetTranscription,
      [pageStatistics.ADDRESS_NUMBER_FILLED]: !!address.number,
      [pageStatistics.ADDRESS_STREET_FILLED]: !!address.street,
      [pageStatistics.ADDRESS_STREET2_FILLED]: !!address.street2,
      [pageStatistics.ADDRESS_PROVINCE_FILLED]: !!address.province,
      [pageStatistics.ADDRESS_CITY_FILLED]: !!address.city,
      [pageStatistics.ADDRESS_COUNTY_FILLED]: !!address.county,
      [pageStatistics.ADDRESS_NICKNAME_FILLED]: !!address.nickname,
      [pageStatistics.DELIVERY_INSTRUCTIONS_FILLED]: !!address.deliveryNotes,
    });
    return network.post(`${config.apiBasePath}/settings/addresses/${id}`, {
      body: JSON.stringify(address),
    })
      .then(() => {
        wa.triggerPageEventWithDescription(
          pageEvents.DELIVERY_ADDRESS_BOOK_OVERRIDE_ADDRESS_SUCCESS_NOTIFICATION_DISPLAYED
        );
        network.redirectTo({ to: '/settings/address-book' });
        dispatch(setSuccessMessage(msg.settings.flashMessage.success));
        return true;
      })
      .catch((response) => {
        if (get(response, 'status') === 412 && get(response, 'data.code')) {
          dispatch(setOverrideDeliveryAddressError(response.data.code));
          return false;
        }

        wa.triggerPageEventWithDescription(
          pageEvents.DELIVERY_ADDRESS_BOOK_OVERRIDE_ADDRESS_ERROR_NOTIFICATION_DISPLAYED
        );
        dispatch(setErrorMessage(msg.settings.flashMessage.failure));
        return false;
      });
  };
}

export function saveAddress() {
  return (dispatch, getState, { network }) => {
    const state = getState();
    const {
      postcode,
      city,
      selectedAddressId,
      manualAddressLine,
      typeAddressManually,
      addressNickname,
      deliveryInstructions,
    } = state.settings.addAddress;

    const payload = {
      premiseId: selectedAddressId,
      prefecture: typeAddressManually ? manualAddressLine : null,
      nickname: addressNickname,
      deliveryNotes: deliveryInstructions,
      postcode,
      city,
    };

    wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_ADD_ADDRESS_SAVE_BUTTON_CLICKED, {
      [pageStatistics.ADDRESS_NICKNAME_FILLED]: !!addressNickname,
      [pageStatistics.DELIVERY_INSTRUCTIONS_FILLED]: !!deliveryInstructions,
      [pageStatistics.ADDRESS_ENTERED_MANUALLY]: typeAddressManually,
    });

    return network.post(`${config.apiBasePath}/settings/addresses`, {
      body: JSON.stringify(payload),
    })
      .then(() => {
        wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_ADD_ADDRESS_SUCCESS_NOTIFICATION_DISPLAYED);
        network.redirectTo({ to: '/settings/address-book' });
        dispatch(setSuccessMessage('New address added'));
        return true;
      })
      .catch((error) => {
        const { data } = error;
        wa.triggerPageEventWithDescription(pageEvents.DELIVERY_ADDRESS_BOOK_ADD_ADDRESS_ERROR_NOTIFICATION_DISPLAYED);
        dispatch(setErrorMessage(data && data.message || msg.settings.flashMessage.failure));
        return false;
      });
  };
}
